// Accordions animate between two measured pixel heights (see accordions.css),
// so the measurements have to be redone whenever their content changes: an htmx
// swap, a lazily loaded embed, a window resize.
//
// Measuring means briefly forcing an accordion open and closed again, which must
// not disturb what the visitor has open. Leaving them all closed is what made
// form accordions snap shut on every step of a multi-step form, and what made
// the Twint accordion on /uber-uns/spenden/ need two clicks to stay open.
function measureHeights(detail) {
  const wasOpen = detail.open

  // Don't animate through the measuring states, and don't animate away from
  // them afterwards either.
  detail.style.setProperty("transition", "none")
  detail.style.removeProperty("--expanded")
  detail.style.removeProperty("--collapsed")

  detail.open = true
  const expanded = detail.offsetHeight
  detail.open = false
  const collapsed = detail.offsetHeight
  detail.open = wasOpen

  detail.style.setProperty("--expanded", `${expanded}px`)
  detail.style.setProperty("--collapsed", `${collapsed}px`)
  void detail.offsetHeight // flush, so nothing transitions from the old height
  detail.style.removeProperty("transition")
}

// Anything that carries no height of its own and never grows.
const NOT_CONTENT = new Set(["SUMMARY", "SCRIPT", "STYLE", "LINK", "TEMPLATE"])

const contentElements = (detail) =>
  Array.prototype.filter.call(
    detail.children,
    (el) => !NOT_CONTENT.has(el.tagName),
  )

export function initDetails() {
  const accordions = () => document.querySelectorAll(".accordion")

  const setAllHeights = () => {
    for (const detail of accordions()) measureHeights(detail)
  }

  // Coalesce bursts: resize fires continuously, and a page can hold a dozen
  // accordions, each costing two forced layouts.
  let queued = 0
  const setAllHeightsSoon = () => {
    if (queued) return
    queued = requestAnimationFrame(() => {
      queued = 0
      setAllHeights()
    })
  }

  // Content that sizes itself after we have measured it -- images, and above all
  // the RaiseNow donation embed in the Twint accordion, which sets its iframe
  // height whenever the iframe gets around to reporting one -- leaves the
  // accordion clipped. Watching the content beats guessing when it is done: that
  // embed dispatches a resize event of its own two seconds in, which routinely
  // fired while the iframe was still growing, so the accordion stayed too short
  // until the visitor happened to resize the window.
  const lastHeights = new WeakMap()
  const observer = new ResizeObserver((entries) => {
    let changed = false
    for (const entry of entries) {
      // Read from the entry, not the element: touching offsetHeight in here
      // forces a layout and risks the "undelivered notifications" loop.
      const height =
        entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
      if (lastHeights.get(entry.target) === height) continue
      lastHeights.set(entry.target, height)
      changed = true
    }
    // Measuring toggles every accordion open and shut, so this observer hears
    // about it too -- but by the end of the frame the content is back to the
    // height already recorded above, so that notification stops here rather
    // than measuring in a loop.
    if (changed) setAllHeightsSoon()
  })

  const observeContent = () => {
    for (const detail of accordions()) {
      for (const el of contentElements(detail)) observer.observe(el)
    }
  }

  setAllHeights()
  observeContent()

  window.addEventListener("load", setAllHeightsSoon)
  window.addEventListener("resize", setAllHeightsSoon)
  window.addEventListener("_opened_fold", setAllHeightsSoon)
  // Registered here, once. Going through onReady(initDetails, true) instead
  // re-ran this whole function on every htmx swap, which re-added each of these
  // listeners -- a page with seven htmx-loaded forms ended up with 24 of them.
  document.addEventListener("htmx:afterSettle", () => {
    observeContent() // swapped-in content needs watching as well
    setAllHeightsSoon()
  })
}
